import React, { useContext, useEffect, useRef, useState } from 'react';
import { PersonalMessageList } from '../../../types/api/PAL/PersonalMessageList';
import { useWidgetContext } from '../Widget';
import PersonalMessage from './partials/PersonalMessage';
import { PALPanelContext } from '../../../_sections/Personal/PersonalAccessLibrary/PALPanelContext';
import ScrollArea from '../../../components/atoms/ScrollArea/ScrollArea';
import { ContentList, ContentListItem } from '../ContentList';
import { AcceptDeclineForm, Checkbox, useForm } from '../../Form2';
import { Button } from '../../../components/molecules/Btn/Button';
import { Icons } from '../../../components/atoms/SVG';
import { BtnModifier } from '../../../components/molecules/Btn';
import { useTranslation } from 'react-i18next';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import ContentMessage from '../ContentMessage/ContentMessage';
import { useDelayed } from '../../../hooks/useDelayed';
import usePrevious from '../../../hooks/usePrevious';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';

export function PersonalMessages({ messageCategory, deleteForm }: PersonalMessageList) {
  const { t } = useTranslation('PersonalMessage');
  const { widgetType } = useWidgetContext();
  const { messagesStates } = useContext(PALPanelContext);
  const { items } = messagesStates?.find(s => s.messageCategory === messageCategory) || { items: [] };

  const [showAcceptDecline, setShowAcceptDecline] = useState(false);
  const [checkedCheckboxes, setCheckedCheckboxes] = useState<string[]>([]);
  const selectAllCheckBoxRef = useRef<HTMLInputElement>(null);
  const deleteButtonRef = useRef<HTMLButtonElement>(null);
  const acceptButtonRef = useRef<HTMLButtonElement>(null);
  const getAllCheckBoxesRef = useRef<HTMLDivElement>(null);
  const previousShowAcceptDecline = usePrevious(showAcceptDecline);

  const delayedOnSuccess = useDelayed(() => {
    setShowAcceptDecline(false);
    setCheckedCheckboxes([]);
  });

  useEffect(() => {
    if (!showAcceptDecline && previousShowAcceptDecline) {
      deleteButtonRef.current?.focus();
    } else {
      acceptButtonRef.current?.focus();
    }
  }, [previousShowAcceptDecline, showAcceptDecline]);

  const { params } = useForm({ form: deleteForm });

  const personalMessageIdParam = params.getByName('personalMessageId');

  const trackCheckedMessages = (checkbox: HTMLInputElement) => {
    if (checkbox.checked) {
      setCheckedCheckboxes([...checkedCheckboxes, checkbox.value]);
    } else {
      setCheckedCheckboxes(checkedCheckboxes.filter(id => id !== checkbox.value));
    }
  };

  return (
    <div ref={getAllCheckBoxesRef}>
      <div className="personal-message__top-container">
        {showAcceptDecline
          ? <ModalDialog
              closeModal={() => setShowAcceptDecline(false)}
              headline={t(`deletableItem.${widgetType}.remove`)}
              initialFocusIndex={1}
            >
              <AcceptDeclineForm
                headline={t(`deletableItem.${widgetType}.removeQuestion`)}
                form={{
                  ...deleteForm,
                  params: [...deleteForm.params,
                    // add selected messages ids as hidden inputs to modal form
                    ...checkedCheckboxes.map(checkBoxValue => ({
                      name: personalMessageIdParam.name, value: checkBoxValue, type: 'hidden',
                    })),
                  ],
                }}
                onSuccess={delayedOnSuccess}
                messageSuccess={t(`deletableItem.${widgetType}.status.success`)}
                messageSending={t(`deletableItem.${widgetType}.status.sending`)}
                messageFail={t(`deletableItem.${widgetType}.status.fail`)}
              />
            </ModalDialog>
          : null }
        <div className="personal-message__delete-header-container">
          <div className="personal-message__checkbox-container">
            <Checkbox
              modifier="single-checkbox-without-text-label"
              ref={selectAllCheckBoxRef}
              label={t(`deletableItem.${widgetType}.selectAll`)}
              labelInvisible
              onChange={(event) => {
                const checkBoxValues: string[] = [];
                const checkBoxes: NodeListOf<HTMLInputElement> | undefined =
                  getAllCheckBoxesRef.current?.querySelectorAll('[type="checkbox"]');
                checkBoxes?.forEach((checkBox: HTMLInputElement) => {
                  if (checkBox !== selectAllCheckBoxRef.current) {
                    checkBox.checked = event.target.checked;

                    if (checkBox.checked) {
                      checkBoxValues.push(checkBox.value);
                    }
                  }
                });

                if (event.target.checked) {
                  setCheckedCheckboxes([...checkedCheckboxes, ...checkBoxValues]);
                } else {
                  setCheckedCheckboxes([]);
                }
              }}
              disabled={items.length === 0}
            />
          </div>
          <Button
            ref={deleteButtonRef}
            type="button"
            title={t(`deletableItem.${widgetType}.remove`)}
            tooltipPlacement={TooltipPlacement.TOP}
            hideTooltipFromScreenReader
            modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
            onClick={() => setShowAcceptDecline(!showAcceptDecline)}
            aria-disabled={checkedCheckboxes.length === 0}
          >
            <Button.Icon svgSpriteId={Icons.DELETE_TRASHCAN} text={t(`deletableItem.${widgetType}.remove`)} />
          </Button>
        </div>
      </div>
      {items?.length === 0
        ? <ContentMessage text={t(`deletableItem.${widgetType}.noContent`)} />
        : <ScrollArea to={100} bottomOffset={10}>
            <ContentList ordered>
              {items.map((item, index) => {
                return (
                  <ContentListItem key={item.id}>
                    <div className="personal-message__container">
                      <div className="personal-message__checkbox-container">
                        <Checkbox
                          modifier="single-checkbox-without-text-label"
                          label={t(`deletableItem.${widgetType}.removeLabel`,
                            { position: index + 1, numberOfItems: items.length })}
                          labelInvisible
                          name={personalMessageIdParam.name}
                          value={item.id}
                          onChange={(event) => {
                            if (selectAllCheckBoxRef.current?.checked) {
                              selectAllCheckBoxRef.current.checked = false;
                            }

                            trackCheckedMessages(event.target);
                          }}
                        />
                      </div>
                      <PersonalMessage {...item.content} />
                    </div>
                  </ContentListItem>
                );
              })}
            </ContentList>
          </ScrollArea>}
    </div>
  );
}
